// =============================================================================
// domain/rMultipleDistribution.ts — the SHAPE of a trader's outcomes.
// -----------------------------------------------------------------------------
// Every other figure on the Analytics tab is an average of some kind. This one is
// the distribution those averages came from, and it answers a question none of
// them can: **where is the edge coming from?**
//
//   fat right tail   a few big winners carry the book — winners are being let run
//   fat left tail    losses running past a full unit of risk — stops not catching
//   clustered at 0   scratchy; lots of trades, very little outcome
//
// A +0.19R mean is the same number whether it came from thirty 3R winners or from
// two hundred trades that all landed near zero, and those are opposite problems.
//
// ── ⚠ THE POPULATION IS TRADES THAT RECORDED AN R, NOT THE PERIOD ──
//
// Only trades carrying a realised R can be binned, and that set is usually much
// smaller than the period — on Josh's book, 218 of 495. The sample travels with
// the shape for that reason: a "fat right tail" over eight trades is not a
// finding about a trader.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

import { median as medianOf } from './excursionStats';

/** Below this the shape is not a shape. */
export const RDIST_MIN_SAMPLE = 5;

export interface RBin {
  label: string;
  /** Inclusive. */
  lo: number;
  /** Exclusive. */
  hi: number;
  count: number;
  /** Share of the binned sample, 0–100. */
  share: number;
}

/**
 * ⚠ THE FIRST LABEL IS `< -2R`, NOT `≤ -2R`.
 *
 * The old client labels it `≤ -2R` while its own condition is `r < -2` — a trade
 * that lost exactly two units of risk lands in the NEXT bin along. On a
 * distribution whose whole job is to show where the tail sits, a boundary label
 * that is off by one bin is worth more than it costs to fix.
 */
const BIN_BOUNDS: readonly { label: string; lo: number; hi: number }[] = [
  { label: '< -2R', lo: -Infinity, hi: -2 },
  { label: '-2 to -1R', lo: -2, hi: -1 },
  { label: '-1 to 0', lo: -1, hi: 0 },
  { label: '0 to 1R', lo: 0, hi: 1 },
  { label: '1 to 2R', lo: 1, hi: 2 },
  { label: '2 to 3R', lo: 2, hi: 3 },
  { label: '3R+', lo: 3, hi: Infinity },
];

/** ⚠ Index 3 and up is favourable — the same split the excursion histogram uses. */
export const RDIST_POSITIVE_FROM = 3;

export type DistShape = 'fat-right' | 'fat-left' | 'scratchy' | 'balanced';

/** A third of the book at a full unit of risk or better. */
export const FAT_RIGHT_SHARE = 30;
/** A quarter of it past a full unit of risk against. */
export const FAT_LEFT_SHARE = 25;
/** Two thirds of it inside ±1R. */
export const SCRATCHY_SHARE = 65;

export interface RDistribution {
  /** Trades carrying a realised R. ⚠ Not the period's trade count. */
  sample: number;
  winners: number;
  losers: number;
  /** Exactly 0R. In neither leg, and in the `0 to 1R` bin. */
  scratches: number;
  median: number | null;
  mean: number | null;
  bins: RBin[];
  shape: DistShape;
  /** At 1R or better. */
  fatRightShare: number;
  /** At −1R or worse. */
  fatLeftShare: number;
  /** Inside ±1R. */
  aroundZeroShare: number;
  /**
   * ⚠ THE SHAPES ARE NOT EXCLUSIVE, and the old client reports only the first
   * that matches. A book can have a fat right tail AND a fat left one — big
   * winners paid for by big losers, which is a different book from one with only
   * the first. Every shape that qualifies is listed, in the same priority order,
   * so the screen can say so instead of choosing silently.
   */
  alsoQualifies: DistShape[];
}

export interface RTrade {
  rrRealized: number | null;
}

/**
 * Bin the realised R of every trade that recorded one.
 *
 * ⚠ `== null` FIRST, as everywhere. `Number(null)` is 0, and a period's worth of
 * unrecorded trades pulled in as exact scratches would land every one of them in
 * the `0 to 1R` bin — manufacturing the scratchy verdict out of missing data.
 */
export function rMultipleDistribution(trades: readonly RTrade[]): RDistribution {
  const rs: number[] = [];
  for (const t of trades) {
    if (t.rrRealized == null) continue;
    const r = Number(t.rrRealized);
    if (Number.isFinite(r)) rs.push(r);
  }

  const sample = rs.length;
  const counts = BIN_BOUNDS.map(() => 0);
  for (const r of rs) {
    for (let i = 0; i < BIN_BOUNDS.length; i += 1) {
      const b = BIN_BOUNDS[i] as { lo: number; hi: number };
      if (r >= b.lo && r < b.hi) { counts[i] = (counts[i] ?? 0) + 1; break; }
    }
  }
  const bins: RBin[] = BIN_BOUNDS.map((b, i) => ({
    ...b,
    count: counts[i] ?? 0,
    share: sample ? (100 * (counts[i] ?? 0)) / sample : 0,
  }));

  const at = (i: number): number => counts[i] ?? 0;
  const fatRightShare = sample ? (100 * (at(4) + at(5) + at(6))) / sample : 0;
  const fatLeftShare = sample ? (100 * (at(0) + at(1))) / sample : 0;
  const aroundZeroShare = sample ? (100 * (at(2) + at(3))) / sample : 0;

  // ⚠ THE ORDER IS THE PRIORITY, and it is the old client's: a fat right tail is
  // the finding a trader can act on first, a fat left tail is the one that costs
  // most, and "scratchy" only applies when neither tail is doing anything.
  const qualifying: DistShape[] = [];
  if (fatRightShare >= FAT_RIGHT_SHARE) qualifying.push('fat-right');
  if (fatLeftShare >= FAT_LEFT_SHARE) qualifying.push('fat-left');
  if (aroundZeroShare >= SCRATCHY_SHARE) qualifying.push('scratchy');

  return {
    sample,
    winners: rs.filter((r) => r > 0).length,
    losers: rs.filter((r) => r < 0).length,
    scratches: rs.filter((r) => r === 0).length,
    median: medianOf(rs),
    mean: sample ? rs.reduce((s, r) => s + r, 0) / sample : null,
    bins,
    shape: qualifying[0] ?? 'balanced',
    fatRightShare, fatLeftShare, aroundZeroShare,
    alsoQualifies: qualifying.slice(1),
  };
}
